import { getStoredAccessToken } from "@/lib/auth-token-storage";
import { expiredLoginPath, tenantSlugFromPath } from "@/lib/safe-next-path";
import { apiUrl } from "@/services/http/api-url";
import { currentSessionEpoch, useAuthStore, type AuthUser } from "@/stores/authStore";

/**
 * C2 HTTP client contract (AUTH-2, AUTH-3, PAY-5). Shared by the axios client and apiFetch:
 *
 *  - `refreshSessionOnce()` — ONE in-flight `POST /auth/refresh` no matter how many requests
 *    401 at once; stores the new token in the auth store.
 *  - `handleUnauthorized()` — what a client does with a 401: refresh, tell it to retry once, or
 *    (refresh refused, user was signed in) clear the session and go to
 *    `/t/<slug>/login?reason=expired&next=<current path>`.
 *  - `ensureFreshAccessToken()` — refresh before sending when the stored token already expired.
 *  - `startSessionKeepAlive()` — proactive refresh shortly before the token expires.
 *  - Expiry is measured on the local clock from the server's lifetime (R3-S13), never `exp` vs
 *    `Date.now()`, and proactive refreshes are at least `MIN_PROACTIVE_REFRESH_MS` apart.
 */

export type RefreshOutcome =
  /** New token stored; retry the request. */
  | "refreshed"
  /** The server refused (401/403): the session is over. */
  | "rejected"
  /** Network / 5xx: unknown, keep the session and let the caller fail normally. */
  | "unavailable";

/** Proactive refresh fires this long before `exp` (the access token lives 15 minutes). */
const REFRESH_LEAD_MS = 60_000;
/** Below this remaining lifetime a request refreshes first instead of sending a dying token. */
const EXPIRY_SKEW_MS = 5_000;
/** A refused refresh is remembered briefly so a burst of 401s doesn't each re-ask the server. */
const REJECTED_MEMORY_MS = 2_000;
/**
 * R3-S13: proactive refreshes (pre-flight and keep-alive) never run more often than this, whatever
 * the token says. A 401 still refreshes at once — that path is bounded by one retry per request.
 */
export const MIN_PROACTIVE_REFRESH_MS = 30_000;

/**
 * Requests whose 401 means "wrong credentials", not "session expired". Refreshing on them
 * would be wrong (and /auth/refresh itself must never recurse).
 */
const CREDENTIAL_PATHS = [
  "/auth/login",
  "/auth/refresh",
  "/auth/logout",
  "/auth/verify-email-otp",
  "/auth/oauth-complete",
  "/auth/test-token",
  "/v1/auth/register",
  "/v1/auth/forgot-password",
  "/v1/auth/reset-password",
];

let inFlight: Promise<RefreshOutcome> | null = null;
let rejectedAt = 0;
/** When the last refresh request was sent (any path), for the proactive floor. */
let lastRefreshAt = 0;
/** R3-S13: local-clock expiry of each token this tab has seen, fixed when it first saw it. */
const localExpiry = new Map<string, number>();
let expiring = false;
/** H3: why the last refresh was refused, when the server said this device was replaced. */
let rejectedReason: "other_device" | "suspended" | "not_approved" | null = null;
let navigateTo: (url: string) => void = (url) => window.location.assign(url);

// A new sign-in re-arms the expiry redirect.
useAuthStore.subscribe((state) => {
  if (state.accessToken) {
    expiring = false;
    rejectedReason = null;
  }
});

/**
 * True between `expireSession()` and the next sign-in. TenantSlugGuard then leaves the
 * `?reason=expired` redirect alone: its own plain sign-in redirect (a same-document navigation)
 * could otherwise land first and cancel the pending page load, losing the reason.
 */
export function isSessionExpiring(): boolean {
  return expiring;
}

/** Override how the app navigates on an expired session (tests; or a router-aware navigate). */
export function setSessionExpiredNavigator(fn: (url: string) => void): void {
  navigateTo = fn;
}

export function resetSessionRefreshForTests(): void {
  try {
    window.localStorage.removeItem(EXPIRY_STORAGE_KEY);
  } catch {
    // ignore
  }
  inFlight = null;
  rejectedAt = 0;
  rejectedReason = null;
  lastRefreshAt = 0;
  localExpiry.clear();
  expiring = false;
}

export function isCredentialEndpoint(url: string): boolean {
  const path = url.replace(/^https?:\/\/[^/]+/i, "").split(/[?#]/)[0] ?? "";
  return CREDENTIAL_PATHS.some((p) => path === p || path.endsWith(p) || path.includes(`${p}/`));
}

function tokenClaims(token: string): { exp?: unknown; iat?: unknown } | null {
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const normalised = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(normalised)) as { exp?: unknown; iat?: unknown };
  } catch {
    return null;
  }
}

/**
 * R3-S13: when `token` expires on THIS browser's clock. `exp` is the server's clock, so comparing
 * it with `Date.now()` breaks on any skew. The lifetime is the server's own `exp − iat` (or
 * `expiresIn` from the response), counted from when this tab first saw the token. A token without
 * `iat` falls back to the raw `exp`.
 */
export function localTokenExpiryMs(token: string | null, expiresInSec?: number): number | null {
  if (!token) return null;
  const known = localExpiry.get(token);
  if (known !== undefined) return known;
  const claims = tokenClaims(token);
  if (!claims || typeof claims.exp !== "number") return null;
  let expiry: number;
  const persisted = readPersistedExpiry(token);
  if (persisted !== null) {
    // SPA-15: seen before this reload — keep the expiry fixed then, not a fresh full lifetime.
    expiry = persisted;
  } else if (typeof expiresInSec === "number" && expiresInSec >= 0) {
    expiry = Date.now() + expiresInSec * 1000;
  } else if (typeof claims.iat === "number") {
    expiry = Date.now() + Math.max(0, claims.exp - claims.iat) * 1000;
  } else {
    expiry = claims.exp * 1000;
  }
  if (localExpiry.size > 20) localExpiry.clear();
  localExpiry.set(token, expiry);
  persistExpiry(token, expiry);
  return expiry;
}

/**
 * SPA-15: the local expiry of the latest token, kept next to it in localStorage so a reload (or a
 * new tab) doesn't treat a nearly-dead token as fresh for another full lifetime. Keyed by a digest
 * of the token; only the latest token is kept.
 */
const EXPIRY_STORAGE_KEY = "lms_access_token_local_expiry";

/** A short FNV-1a digest of the whole token: identifies it without storing a second copy. */
function tokenId(token: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < token.length; i += 1) {
    hash ^= token.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${token.length}:${hash.toString(36)}`;
}

function readPersistedExpiry(token: string): number | null {
  try {
    const raw = window.localStorage.getItem(EXPIRY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: unknown; at?: unknown };
    return parsed.id === tokenId(token) && typeof parsed.at === "number" ? parsed.at : null;
  } catch {
    return null;
  }
}

function persistExpiry(token: string, expiry: number): void {
  try {
    window.localStorage.setItem(
      EXPIRY_STORAGE_KEY,
      JSON.stringify({ id: tokenId(token), at: expiry }),
    );
  } catch {
    // Storage blocked: a reload falls back to the token's own lifetime.
  }
}

async function performRefresh(): Promise<RefreshOutcome> {
  // SPA-8: a sign-out (or another sign-in) while this is in flight wins over its answer.
  const epoch = currentSessionEpoch();
  const superseded = () => currentSessionEpoch() !== epoch;
  let res: Response;
  try {
    // /api/auth/* is CSRF-exempt; the refresh cookie is the credential.
    res = await fetch(apiUrl("/auth/refresh"), { method: "POST", credentials: "include" });
  } catch {
    return "unavailable";
  }
  if (superseded()) {
    if (useAuthStore.getState().accessToken) return "refreshed";
    // Signed out while the server renewed the session: end that session too. R-20: name it by
    // the answer's own access token and send no cookies — the refresh cookie is shared by every
    // tab, and another tab may have signed in (and set its own) since.
    if (res.ok) void endSupersededSession(res);
    return "unavailable";
  }
  if (res.status === 401 || res.status === 403) {
    const code = await refusalCode(res);
    // H3: replaced by another device; H4: the school suspended the account; W-29: the email was
    // removed from the school's approved list.
    rejectedReason =
      code === "SIGNED_OUT_OTHER_DEVICE"
        ? "other_device"
        : code === "ACCOUNT_SUSPENDED"
          ? "suspended"
          : code === "EMAIL_NOT_APPROVED"
            ? "not_approved"
            : null;
    return "rejected";
  }
  if (!res.ok) return "unavailable";
  try {
    const body = (await res.json()) as {
      data?: { accessToken?: string; expiresIn?: number; user?: AuthUser };
    };
    const accessToken = body.data?.accessToken;
    const user = body.data?.user ?? useAuthStore.getState().user;
    // Signed in again while the body was read: the new session's token is stored.
    if (superseded()) return useAuthStore.getState().accessToken ? "refreshed" : "unavailable";
    if (!accessToken || !user) return "unavailable";
    localTokenExpiryMs(accessToken, body.data?.expiresIn);
    useAuthStore.getState().renewSession(accessToken, user);
    return "refreshed";
  } catch {
    return "unavailable";
  }
}

async function refusalCode(res: Response): Promise<string | null> {
  try {
    const body = (await res.json()) as { error?: { code?: unknown } };
    return typeof body.error?.code === "string" ? body.error.code : null;
  } catch {
    return null;
  }
}

async function endSupersededSession(res: Response): Promise<void> {
  try {
    const body = (await res.json()) as { data?: { accessToken?: string } };
    const token = body.data?.accessToken;
    if (!token) return;
    await fetch(apiUrl("/auth/logout"), {
      method: "POST",
      credentials: "omit",
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    // Best effort: logout already ended this session in the normal case.
  }
}

/**
 * I-6: the longest a sign-in waits for a refresh in flight. A refresh normally answers in well
 * under a second; this only bounds a hung one.
 */
export const SIGN_IN_REFRESH_WAIT_MS = 10_000;

/** Endpoints whose answer sets a new session's refresh cookie. */
const SESSION_ISSUING_PATHS = [
  "/auth/login",
  "/auth/verify-email-otp",
  "/auth/oauth-complete",
  "/auth/test-token",
];

function issuesSession(url: string): boolean {
  const path = url.replace(/^https?:\/\/[^/]+/i, "").split(/[?#]/)[0] ?? "";
  return SESSION_ISSUING_PATHS.some(
    (p) => path === p || path.endsWith(p) || path.startsWith(`${p}/`),
  );
}

/**
 * I-6: a refresh in flight answers with `Set-Cookie: lms_refresh_token=<its session>` (or a clear
 * on 401). Landing after a new sign-in's own Set-Cookie, it would replace the new session's cookie
 * and sign that user out at their next refresh. So a sign-in first lets it land (bounded).
 */
export async function settleRefreshBeforeSignIn(url: string): Promise<void> {
  if (!inFlight || !issuesSession(url)) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    inFlight.catch(() => undefined),
    new Promise<void>((resolve) => {
      timer = setTimeout(resolve, SIGN_IN_REFRESH_WAIT_MS);
    }),
  ]);
  clearTimeout(timer);
}

/** Single-flight refresh shared by every HTTP client in the SPA. */
export function refreshSessionOnce(): Promise<RefreshOutcome> {
  if (inFlight) return inFlight;
  if (Date.now() - rejectedAt < REJECTED_MEMORY_MS) return Promise.resolve("rejected");
  lastRefreshAt = Date.now();
  inFlight = performRefresh().then((outcome) => {
    if (outcome === "rejected") rejectedAt = Date.now();
    inFlight = null;
    return outcome;
  });
  return inFlight;
}

function wasSignedIn(): boolean {
  return Boolean(getStoredAccessToken() || useAuthStore.getState().isAuthenticated);
}

/** Clear the session and send the user to their tenant's login with `?reason=expired&next=`. */
export function expireSession(): void {
  if (expiring) return;
  expiring = true;
  const { pathname, search, hash } = window.location;
  const slug = tenantSlugFromPath(pathname) ?? useAuthStore.getState().user?.tenantSlug ?? null;
  useAuthStore.getState().clearSession();
  const reason = rejectedReason ?? "expired";
  navigateTo(slug ? expiredLoginPath(slug, `${pathname}${search}${hash}`, reason) : "/");
}

/**
 * Called by a client that got a 401 for `url` (on the first attempt only). `true` means
 * "a new token is stored — retry once"; `false` means return the 401 to the caller.
 */
export async function handleUnauthorized(url: string): Promise<boolean> {
  if (isCredentialEndpoint(url)) return false;
  const signedIn = wasSignedIn();
  const outcome = await refreshSessionOnce();
  if (outcome === "refreshed") return true;
  if (outcome === "rejected" && signedIn) expireSession();
  return false;
}

/**
 * Before sending: if the stored token has (nearly) expired, refresh first. A sign-in instead waits
 * for a refresh in flight to land (I-6).
 */
export async function ensureFreshAccessToken(url: string): Promise<void> {
  if (isCredentialEndpoint(url)) {
    await settleRefreshBeforeSignIn(url);
    return;
  }
  // W-23: a school switch (signed in, so not a credential endpoint) also issues a session.
  await settleRefreshBeforeSignIn(url);
  const exp = localTokenExpiryMs(getStoredAccessToken());
  if (exp === null || exp - Date.now() > EXPIRY_SKEW_MS) return;
  if (!inFlight && Date.now() - lastRefreshAt < MIN_PROACTIVE_REFRESH_MS) return;
  await refreshSessionOnce();
}

/**
 * Keep the access token fresh while the app is open: schedule a refresh `REFRESH_LEAD_MS`
 * before each token's `exp`, rescheduling whenever the token changes. Returns a stop function.
 * A failed proactive refresh does nothing on its own — the next request's 401 handles it.
 */
export function startSessionKeepAlive(): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const schedule = (token: string | null) => {
    if (timer) clearTimeout(timer);
    timer = null;
    const exp = localTokenExpiryMs(token);
    if (exp === null) return;
    const sinceLast = Date.now() - lastRefreshAt;
    const delay = Math.max(
      exp - Date.now() - REFRESH_LEAD_MS,
      1_000,
      MIN_PROACTIVE_REFRESH_MS - sinceLast,
    );
    timer = setTimeout(() => {
      timer = null;
      void refreshSessionOnce();
    }, delay);
  };

  schedule(useAuthStore.getState().accessToken);
  const unsubscribe = useAuthStore.subscribe((state, prev) => {
    if (state.accessToken !== prev.accessToken) schedule(state.accessToken);
  });

  return () => {
    unsubscribe();
    if (timer) clearTimeout(timer);
  };
}
